/**
 * stripeService.ts
 * OH3 Stripe決済 フロント側ユーティリティ
 *
 * Stripe Checkout Session方式:
 * 1. create → Checkout URLを取得
 * 2. ユーザーをStripe Checkoutにリダイレクト
 * 3. 支払い完了後にOH3に戻る（stripe_session パラメータ付き）
 * 4. verify → セッション検証＋DB更新
 */

import { phpApiPost, phpApiGet } from '@/js/phpApi'
import store from '@/store'

// 型定義
export interface CheckoutResult {
  success: boolean
  error?: string
  checkout_url?: string
  session_id?: string
}

export interface VerifyResult {
  success: boolean
  error?: string
  plan_type?: string
  subscription_end?: string
  message?: string
}

export interface PortalResult {
  success: boolean
  error?: string
  portal_url?: string
}

export interface SubscriptionStatus {
  success: boolean
  error?: string
  has_subscription?: boolean
  plan_type?: string
  status?: string
  current_period_end?: string
  stripe_subscription_id?: string
}

/**
 * Stripe Checkout Sessionを作成し、Checkout URLを返す
 *
 * @param planType - 'personal', 'pro', 'office', 'office_pro', or 'unlimited'
 * @returns CheckoutResult（checkout_url を使ってリダイレクト）
 */
export async function createCheckoutSession(planType: 'personal' | 'pro' | 'office' | 'office_pro' | 'unlimited' = 'personal', orgName?: string): Promise<CheckoutResult> {
  const userId = store.state.userId
  if (!userId) {
    return { success: false, error: 'NOT_LOGGED_IN' }
  }

  try {
    // Firebase Authからユーザーのメールアドレスを取得
    const email = (store.state as any).authUser?.email || ''

    const params: Record<string, string> = {
      action: 'create_checkout',
      userId,
      planType,
      baseUrl: window.location.origin + window.location.pathname,
      email
    }
    if (orgName) {
      params.orgName = orgName
    }
    // オーナーのニックネーム用にdisplayNameを渡す
    const displayName = localStorage.getItem('lastNickname') || ''
    if (displayName) {
      params.displayName = displayName
    }
    const result = await phpApiPost<CheckoutResult>('stripeApi.php', params)

    if (result.success && result.checkout_url) {
    } else {
      console.warn('[StripeService] Checkout Session作成失敗:', result.error)
    }

    return result
  } catch (error) {
    console.error('[StripeService] createCheckoutSession failed:', error)
    return { success: false, error: 'NETWORK_ERROR' }
  }
}

/**
 * Checkout完了後のセッション検証
 * URLパラメータの stripe_session から呼ばれる
 *
 * @param sessionId - Stripe Checkout Session ID (cs_xxx)
 * @returns VerifyResult
 */
export async function verifyCheckoutSession(sessionId: string): Promise<VerifyResult> {
  if (!sessionId) {
    return { success: false, error: 'MISSING_SESSION_ID' }
  }

  try {
    const result = await phpApiPost<VerifyResult>('stripeApi.php', {
      action: 'verify_session',
      sessionId
    })

    if (result.success) {
      // トークン残高キャッシュをクリアして最新を取得
      const { clearBalanceCache, getTokenBalance } = await import('@/js/tokenService')
      clearBalanceCache()
      await getTokenBalance()
    }

    return result
  } catch (error) {
    console.error('[StripeService] verifyCheckoutSession failed:', error)
    return { success: false, error: 'NETWORK_ERROR' }
  }
}

/**
 * Stripe Customer Portal URLを取得
 * サブスク管理（解約・支払方法変更）用
 *
 * @returns PortalResult（portal_url でリダイレクト）
 */
export async function getCustomerPortalUrl(): Promise<PortalResult> {
  const userId = store.state.userId
  if (!userId) {
    return { success: false, error: 'NOT_LOGGED_IN' }
  }

  try {
    const result = await phpApiPost<PortalResult>('stripeApi.php', {
      action: 'customer_portal',
      userId,
      baseUrl: window.location.origin + window.location.pathname
    })

    return result
  } catch (error) {
    console.error('[StripeService] getCustomerPortalUrl failed:', error)
    return { success: false, error: 'NETWORK_ERROR' }
  }
}

/**
 * サブスクリプション状態を取得
 *
 * @returns SubscriptionStatus
 */
export async function getSubscriptionStatus(): Promise<SubscriptionStatus> {
  const userId = store.state.userId
  if (!userId) {
    return { success: false, error: 'NOT_LOGGED_IN' }
  }

  try {
    const result = await phpApiGet<SubscriptionStatus>('stripeApi.php', {
      action: 'subscription_status',
      userId
    })

    return result
  } catch (error) {
    console.error('[StripeService] getSubscriptionStatus failed:', error)
    return { success: false, error: 'NETWORK_ERROR' }
  }
}

/**
 * URLパラメータからStripe関連のパラメータをチェック
 * App.vue の mounted で呼ぶ
 */
export function checkStripeRedirect(): { sessionId: string | null, result: string | null } {
  const params = new URLSearchParams(window.location.search)
  const sessionId = params.get('stripe_session')
  const result = params.get('stripe_result')

  // URLからStripeパラメータを削除（履歴に残さない）
  if (sessionId || result) {
    const url = new URL(window.location.href)
    url.searchParams.delete('stripe_session')
    url.searchParams.delete('stripe_result')
    window.history.replaceState({}, '', url.toString())
  }

  return { sessionId, result }
}
